import React, { useEffect, useRef, useCallback, memo } from 'react';
import { usePrevious } from 'js/hooks/usePrevious';
import { clsb, clsx } from 'js/utils/classNames.util';

import * as Types from '../../types';

import style from './AutocompleteOption.scss';

export const AutocompleteOption: React.FC<Types.IAutocompleteOptionProps> = memo((props) => {
    const {
        activeValue,
        chosenValue,
        className,
        classNames,
        fieldId,
        firstItemIndex,
        itemHeight,
        Option,
        onOptionClick,
        option,
        optionIndex,
        optionsContainerRef
    } = props;
    const { value, label } = option;
    const optionRef = useRef<HTMLLIElement>(null);
    const prevActiveValue = usePrevious(activeValue);

    const handleDocumentKeyDown = useCallback((event: KeyboardEvent): void => {
        if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
            event.preventDefault();
        }

        if (event.key === 'Enter') {
            onOptionClick(option);
        }
    }, [onOptionClick, option]);

    useEffect(() => {
        if (prevActiveValue === activeValue || !optionRef.current || !optionsContainerRef.current) {
            return;
        }

        if (activeValue === value) {
            optionsContainerRef.current.scrollTo(0, optionRef.current.offsetTop);
            document.addEventListener('keydown', handleDocumentKeyDown, { once: true });
        }
    }, [optionsContainerRef, value, activeValue, prevActiveValue, handleDocumentKeyDown]);

    return (
        <li
            ref={optionRef}
            onClick={() => onOptionClick(option)}
            aria-selected={value === activeValue}
            style={itemHeight ? { top: `${(firstItemIndex + optionIndex) * itemHeight}px` } : {}}
            id={`${fieldId}_${value}`} // Needed for accessibility: used as value in "aria-activedescendant"
            className={clsx([
                className,
                style.option,
                'cms-autocomplete-field-option',
                value === activeValue ? clsb([style, classNames], ['activeOption']) : '',
                value === chosenValue ? clsb([style, classNames], ['selectedOption']) : ''
            ])}
        >
            {Option ? <Option {...option } /> : label}
        </li>
    );
});
